import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { useToast } from "../lib/toast";
import AccountMappingModal from "../components/AccountMappingModal";
import { AppShell, Topbar } from "../components/Layout";
import FileSidebar, { CATALOGUE, ALL_TYPES } from "../components/FileSidebar";
import { functionStatus } from "../lib/functionStatus";
import { Button, Card, Skeleton, Spinner, cx } from "../components/ui";
import {
  FUNCTION_META,
  TONE_CLASSES,
  BoxIcon,
  SettingsIcon,
  ArrowRightIcon,
  CheckIcon,
  SparkIcon,
} from "../components/ui/Icons";

// Reference data (accounts, contacts, VAT codes, bank accounts, items) used to
// be pulled by a "Refresh reference data" button here. Every push now calls
// ensureReferenceData first, so the button only ever caused doubt about whether
// it was a step someone had forgotten. The export endpoints it used are still
// live — ensureReferenceData calls them server-side.

export default function FileDashboard() {
  const { fileId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [file, setFile] = useState(null);
  const [user, setUser] = useState(null);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAccountMapping, setShowAccountMapping] = useState(false);
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/api/auth/me"),
      api.get(`/api/files/${fileId}`),
      api.get(`/api/file-functions/${fileId}/function`),
    ])
      .then(([u, f, funcs]) => {
        setUser(u.data);
        setFile(f.data);
        setFunctions(funcs.data);
      })
      .catch((err) => toast.error("Could not load file", errMsg(err)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  const openFunction = async (type) => {
    setOpening(type);
    try {
      const res = await api.post(`/api/file-functions/${fileId}/function`, { type });
      navigate(`/file/${fileId}/function/${res.data._id}`);
    } catch (err) {
      toast.error("Could not open", errMsg(err));
      setOpening(null);
    }
  };

  if (loading) {
    return (
      <AppShell sidebar={<FileSidebar file={null} user={null} fileId={fileId} />}>
        <Topbar title="Loading…" />
        <main className="flex-1 space-y-6 px-8 py-7">
          <Skeleton className="h-40 w-full rounded-2xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </main>
      </AppShell>
    );
  }

  const byType = {};
  for (const fn of [...functions].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))) {
    byType[fn.type] = fn;
  }
  const done = ALL_TYPES.filter(
    (t) => functionStatus(byType[t]).key === "pushed"
  ).length;
  const pct = Math.round((done / ALL_TYPES.length) * 100);

  return (
    <AppShell sidebar={<FileSidebar file={file} user={user} fileId={fileId} />}>
      <Topbar
        title={file?.fileName || "File"}
        orgSlug={file?.naqood?.organizationSlug}
        actions={
          <>
            {/* Account Mapping — hidden from the toolbar on request
                (2026-08-25). Everything behind it is untouched: the modal,
                its state and the API it calls all still work. Delete these
                comment markers to bring the button back. */}
            {/*
            <Button variant="secondary" icon={SettingsIcon} onClick={() => setShowAccountMapping(true)}>
              Account Mapping
            </Button>
            */}
          </>
        }
      />

      <main className="flex-1 space-y-6 bg-mesh-light px-8 py-7">
        {/* Progress */}
        <Card className="animate-slide-up overflow-hidden">
          <div className="flex flex-wrap items-center gap-5 p-6">
            <div className="relative grid h-16 w-16 shrink-0 place-items-center">
              <svg viewBox="0 0 36 36" className="h-16 w-16 -rotate-90">
                <circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="4"
                  className="stroke-ink-200" />
                <circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="4"
                  strokeLinecap="round" className="stroke-brand-500 transition-all duration-700"
                  strokeDasharray={`${(pct / 100) * 97.4} 97.4`} />
              </svg>
              <span className="absolute text-[13px] font-bold tabular-nums text-ink-800">
                {pct}%
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <h2 className="text-[17px] font-semibold tracking-tight text-ink-900">
                {done === 0
                  ? "Let's get started"
                  : done === ALL_TYPES.length
                  ? "Everything is pushed"
                  : "Import in progress"}
              </h2>
              <p className="mt-0.5 text-[13.5px] text-ink-500">
                {done} of {ALL_TYPES.length} imports are fully pushed to Naqood. Pick one
                from the sidebar to upload and push.
              </p>
            </div>

            <div className="flex items-center gap-1.5 rounded-xl bg-brand-50 px-3.5 py-2.5 text-[12.5px] text-brand-800 ring-1 ring-inset ring-brand-100">
              <SparkIcon size={14} />
              Reference data syncs automatically
            </div>
          </div>
        </Card>

        {/* Quick pick */}
        <div className="space-y-5">
          {CATALOGUE.map(({ group, hint, types }, gi) => (
            <div key={group} className="animate-slide-up"
              style={{ animationDelay: `${60 + gi * 40}ms` }}>
              <div className="mb-2.5 flex flex-wrap items-baseline gap-x-2.5 px-1">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-ink-400">
                  {group}
                </p>
                {hint && (
                  <p className="text-[12px] text-ink-400">{hint}</p>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {types.map((type) => {
                  const meta = FUNCTION_META[type] || { Icon: BoxIcon, tone: "slate", label: type };
                  const { Icon, tone, label } = meta;
                  const status = functionStatus(byType[type]);
                  const busy = opening === type;

                  return (
                    <button
                      key={type}
                      onClick={() => openFunction(type)}
                      disabled={busy}
                      className={cx(
                        "group relative overflow-hidden rounded-2xl border p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-card-hover",
                        status.key === "pushed"
                          ? "border-emerald-200 bg-gradient-to-br from-emerald-50/70 to-white hover:border-emerald-300"
                          : status.key === "empty"
                          ? "border-ink-200/70 bg-white hover:border-brand-300"
                          : "border-amber-200 bg-gradient-to-br from-amber-50/60 to-white hover:border-amber-300",
                        busy && "opacity-70"
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <span className={cx(
                          "grid h-11 w-11 place-items-center rounded-xl ring-1 transition group-hover:scale-105",
                          TONE_CLASSES[tone]
                        )}>
                          {busy ? <Spinner size={18} /> : <Icon size={19} />}
                        </span>

                        {status.key === "pushed" ? (
                          <span
                            title={status.label}
                            className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white shadow-sm"
                          >
                            <CheckIcon size={12} strokeWidth={3} />
                          </span>
                        ) : status.key !== "empty" ? (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-200">
                            {status.key === "partial" ? status.short : "Not pushed"}
                          </span>
                        ) : null}
                      </div>

                      <p className="mt-3 truncate text-[14px] font-semibold text-ink-900">
                        {label}
                      </p>
                      <p className="mt-0.5 flex items-center gap-1 truncate text-[12px] text-ink-500">
                        {status.label}
                        <ArrowRightIcon
                          size={12}
                          className="shrink-0 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100"
                        />
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </main>

      {showAccountMapping && (
        <AccountMappingModal fileId={fileId} onClose={() => setShowAccountMapping(false)} />
      )}
    </AppShell>
  );
}
